import React, {useState} from 'react';
import {Button, Checkbox, FlexColumn, FlexRow, Label, Text, TextField} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {isCustomerPortalAvailable} from '../../shared/util/customerPortalAvailability';
import Util from '../../shared/util/util';
import provisionPortalUserMutation from '../../../mutations/provision_portal_user_mutation';
import updatePortalUserMutation from '../../../mutations/update_portal_user_mutation';
import invitePortalUserMutation from '../../../mutations/invite_portal_user_mutation';
import generatePortalUserInviteLinkMutation from '../../../mutations/generate_portal_user_invite_link_mutation';

// Clean, non-null shapes the component's render code works with. The raw Relay
// fragment (below) is deeply nullable + readonly; we normalize into these once at
// the top of the component so the render body needn't null-check every field.
type PortalUser = {
	id: string;
	email: string;
	roleKey: string;
	status: string;
	scopeLevel: string;
	scopeProgrammeIds: string[];
	scopeProjectIds: string[];
};

type HierarchyNode = {
	id: string;
	name: string;
	programmeId?: string;
};

// The prop shape mirrors the Relay-generated fragment type
// (EditClientDetailsModal_viewer.graphql.ts): every leaf is nullable and lists are
// ReadonlyArray, because Relay types every selected field as potentially null.
type RawPortalUser = {
	readonly id: string | null;
	readonly email: string | null;
	readonly roleKey: string | null;
	readonly status: string | null;
	readonly scopeLevel: string | null;
	readonly scopeProgrammeIds: ReadonlyArray<string | null> | null;
	readonly scopeProjectIds: ReadonlyArray<string | null> | null;
};

type RawHierarchyNode = {
	readonly id: string | null;
	readonly name: string | null;
	readonly programmeId?: string | null;
};

type ClientData = {
	readonly id: string;
	readonly name: string;
	readonly portalConfig?: {
		readonly portalActive: boolean | null;
	} | null;
	// Relay types list elements as nullable too (ReadonlyArray<{...} | null>).
	readonly portalUsers?: ReadonlyArray<RawPortalUser | null> | null;
	readonly clientPortalHierarchy?: {
		readonly programmes: ReadonlyArray<RawHierarchyNode | null> | null;
		readonly projects: ReadonlyArray<RawHierarchyNode | null> | null;
	} | null;
};

const nonNull = <T,>(value: T | null): value is T => value != null;

const nonNullStrings = (values: ReadonlyArray<string | null> | null | undefined): string[] =>
	(values ?? []).filter((value): value is string => value != null);

const toHierarchyNode = (node: RawHierarchyNode): HierarchyNode => ({
	id: node.id ?? '',
	name: node.name ?? '',
	programmeId: node.programmeId ?? undefined,
});

type WorkspaceUsersSectionProps = {
	client: ClientData;
	// The modal's LIVE "Activate workspace" toggle. The section appears as soon as
	// the admin flips it on in-session, rather than waiting for a save + fragment
	// refetch. Falls back to the persisted portalConfig.portalActive when omitted.
	workspaceActive?: boolean;
};

const CLIENT_ROLES = [
	{key: 'client_executive', labelId: 'client_workspace.users.role.client_executive'},
	{key: 'active_collaborator', labelId: 'client_workspace.users.role.active_collaborator'},
	{key: 'billing_contact', labelId: 'client_workspace.users.role.billing_contact'},
];

const WorkspaceUsersSection = ({client, workspaceActive}: WorkspaceUsersSectionProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const [showInviteForm, setShowInviteForm] = useState(false);
	const [inviteEmail, setInviteEmail] = useState('');
	const [inviteRole, setInviteRole] = useState<string>('client_executive');
	const [inviteScopeLevel, setInviteScopeLevel] = useState<'client' | 'scoped'>('client');
	const [selectedProgrammes, setSelectedProgrammes] = useState<string[]>([]);
	const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
	const [revealedLinks, setRevealedLinks] = useState<Record<string, string>>({});

	// Gate: availability + workspace active. Prefer the modal's live toggle so the
	// section appears immediately on activation; fall back to the persisted value.
	const isActive = workspaceActive ?? !!client.portalConfig?.portalActive;
	if (!isCustomerPortalAvailable() || !isActive) {
		return null;
	}

	// Normalize the nullable Relay shape into the non-null types the render uses.
	// Both the lists and their elements are nullable, so drop null entries first.
	const users: PortalUser[] = (client.portalUsers ?? []).filter(nonNull).map(user => ({
		id: user.id ?? '',
		email: user.email ?? '',
		roleKey: user.roleKey ?? '',
		status: user.status ?? '',
		scopeLevel: user.scopeLevel ?? 'client',
		scopeProgrammeIds: nonNullStrings(user.scopeProgrammeIds),
		scopeProjectIds: nonNullStrings(user.scopeProjectIds),
	}));
	const hierarchy: {programmes: HierarchyNode[]; projects: HierarchyNode[]} = {
		programmes: (client.clientPortalHierarchy?.programmes ?? []).filter(nonNull).map(toHierarchyNode),
		projects: (client.clientPortalHierarchy?.projects ?? []).filter(nonNull).map(toHierarchyNode),
	};

	const handleInvite = () => {
		const input = {
			clientOrgId: client.id,
			email: inviteEmail,
			roleKey: inviteRole,
			clientName: client.name,
			scopeLevel: inviteScopeLevel,
			scopeProgrammeIds: inviteScopeLevel === 'scoped' ? selectedProgrammes : [],
			scopeProjectIds: inviteScopeLevel === 'scoped' ? selectedProjects : [],
		};

		Util.CommitMutation(provisionPortalUserMutation, input, () => {
			setShowInviteForm(false);
			setInviteEmail('');
			setSelectedProgrammes([]);
			setSelectedProjects([]);
		});
	};

	const handleResendInvite = (userId: string) => {
		Util.CommitMutation(invitePortalUserMutation, {
			clientOrgId: client.id,
			userId,
		});
	};

	const handleDeactivate = (userId: string) => {
		Util.CommitMutation(updatePortalUserMutation, {
			clientOrgId: client.id,
			userId,
			deactivated: true,
		});
	};

	const handleReactivate = (userId: string) => {
		Util.CommitMutation(updatePortalUserMutation, {
			clientOrgId: client.id,
			userId,
			deactivated: false,
		});
	};

	const handleGetLoginLink = (userId: string) => {
		Util.CommitMutation(generatePortalUserInviteLinkMutation, {clientOrgId: client.id, userId}, (response: unknown) => {
			const url = (response as {generatePortalUserInviteLink?: {url?: string | null}})?.generatePortalUserInviteLink?.url;
			if (url) {
				setRevealedLinks(prev => ({...prev, [userId]: url}));
			}
		});
	};

	const handleCopyLink = (url: string) => {
		navigator.clipboard.writeText(url);
	};

	const getStatusLabel = (status: string): string => {
		const statusMap: Record<string, string> = {
			invited: formatMessage({id: 'client_workspace.users.status.invited'}),
			active: formatMessage({id: 'client_workspace.users.status.active'}),
			deactivated: formatMessage({id: 'client_workspace.users.status.deactivated'}),
			not_invited: formatMessage({id: 'client_workspace.users.status.not_invited'}),
			invite_expired: formatMessage({id: 'client_workspace.users.status.invite_expired'}),
		};
		return statusMap[status] || status;
	};

	const getRoleLabel = (roleKey: string): string => {
		const role = CLIENT_ROLES.find(r => r.key === roleKey);
		return role ? formatMessage({id: role.labelId}) : roleKey;
	};

	const getVisibilityLabel = (user: PortalUser): string => {
		if (user.scopeLevel === 'client') {
			return formatMessage({id: 'client_workspace.users.visibility.client'});
		}
		const scopedCount = (user.scopeProgrammeIds?.length || 0) + (user.scopeProjectIds?.length || 0);
		return `${scopedCount} ${formatMessage({id: 'client_workspace.users.visibility.scoped'})}`;
	};

	const toggleProgramme = (progId: string) => {
		setSelectedProgrammes(prev => (prev.includes(progId) ? prev.filter(id => id !== progId) : [...prev, progId]));
	};

	const toggleProject = (projId: string) => {
		setSelectedProjects(prev => (prev.includes(projId) ? prev.filter(id => id !== projId) : [...prev, projId]));
	};

	return (
		<FlexColumn gap={'l'} data-cy="workspace-users-section">
			{/* Section divider */}
			<FlexRow gap={'s'} alignItems={'center'}>
				<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
				<Label>
					<FormattedMessage id="client_workspace.users.title" />
				</Label>
				<div style={{flex: 1, height: 1, background: 'var(--border-decorative)'}} />
			</FlexRow>

			{/* Users table */}
			{users.length === 0 ? (
				<Text color={'secondary'}>
					<FormattedMessage id="client_workspace.users.no_users" />
				</Text>
			) : (
				<div style={{overflowX: 'auto'}}>
					<table style={{width: '100%', borderCollapse: 'collapse'}}>
						<thead>
							<tr style={{borderBottom: '1px solid var(--border-decorative)'}}>
								<th style={{textAlign: 'left', padding: '8px'}}>
									<Text>
										<FormattedMessage id="client_workspace.users.email" />
									</Text>
								</th>
								<th style={{textAlign: 'left', padding: '8px'}}>
									<Text>
										<FormattedMessage id="client_workspace.users.role" />
									</Text>
								</th>
								<th style={{textAlign: 'left', padding: '8px'}}>
									<Text>
										<FormattedMessage id="client_workspace.users.visibility" />
									</Text>
								</th>
								<th style={{textAlign: 'left', padding: '8px'}}>
									<Text>
										<FormattedMessage id="client_workspace.users.status" />
									</Text>
								</th>
								<th style={{textAlign: 'left', padding: '8px'}}>
									<Text>
										<FormattedMessage id="client_workspace.users.actions" />
									</Text>
								</th>
							</tr>
						</thead>
						<tbody>
							{users.map(user => (
								<React.Fragment key={user.id}>
									<tr style={{borderBottom: '1px solid var(--border-decorative)'}}>
										<td style={{padding: '8px'}}>
											<Text>{user.email}</Text>
										</td>
										<td style={{padding: '8px'}}>
											<Text>{getRoleLabel(user.roleKey)}</Text>
										</td>
										<td style={{padding: '8px'}}>
											<Text>{getVisibilityLabel(user)}</Text>
										</td>
										<td style={{padding: '8px'}}>
											<Text>{getStatusLabel(user.status)}</Text>
										</td>
										<td style={{padding: '8px'}}>
											<FlexRow gap={'xs'}>
												{(user.status === 'not_invited' || user.status === 'invite_expired') && (
													<Button
														emphasis="low"
														onPress={() => handleResendInvite(user.id)}
														data-cy={`send-invite-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.send_invite'})}
													</Button>
												)}
												{user.status === 'invited' && (
													<Button
														emphasis="low"
														onPress={() => handleResendInvite(user.id)}
														data-cy={`resend-invite-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.resend_invite'})}
													</Button>
												)}
												{user.status !== 'deactivated' && (
													<Button
														emphasis="low"
														onPress={() => handleGetLoginLink(user.id)}
														data-cy={`get-login-link-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.get_login_link'})}
													</Button>
												)}
												<Button emphasis="low" data-cy={`edit-${user.id}`}>
													{formatMessage({id: 'client_workspace.users.edit'})}
												</Button>
												{user.status !== 'deactivated' ? (
													<Button
														emphasis="low"
														onPress={() => handleDeactivate(user.id)}
														data-cy={`deactivate-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.deactivate'})}
													</Button>
												) : (
													<Button
														emphasis="low"
														onPress={() => handleReactivate(user.id)}
														data-cy={`reactivate-${user.id}`}
													>
														{formatMessage({id: 'client_workspace.users.reactivate'})}
													</Button>
												)}
											</FlexRow>
										</td>
									</tr>
									{revealedLinks[user.id] && (
										<tr>
											<td colSpan={5} style={{padding: '8px'}}>
												<FlexColumn gap={'xs'}>
													<Label>
														<FormattedMessage id="client_workspace.users.login_link_label" />
													</Label>
													<FlexRow gap={'s'} alignItems={'center'}>
														<TextField
															value={revealedLinks[user.id]}
															disabled
															width="flex"
															data-cy={`login-link-field-${user.id}`}
															data-private
														/>
														<Button
															emphasis="low"
															onPress={() => handleCopyLink(revealedLinks[user.id])}
														>
															{formatMessage({id: 'client_workspace.users.copy_link'})}
														</Button>
													</FlexRow>
													<Text color={'secondary'}>
														<FormattedMessage id="client_workspace.users.login_link_expiry" />
													</Text>
												</FlexColumn>
											</td>
										</tr>
									)}
								</React.Fragment>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Invite user button/form */}
			{!showInviteForm ? (
				<Button emphasis="medium" onPress={() => setShowInviteForm(true)} data-cy="show-invite-form">
					<FormattedMessage id="client_workspace.users.invite_button" />
				</Button>
			) : (
				<FlexColumn
					gap={'m'}
					style={{padding: '16px', border: '1px solid var(--border-decorative)', borderRadius: '4px'}}
				>
					<Label>
						<FormattedMessage id="client_workspace.users.invite_form.title" />
					</Label>

					<TextField
						label={formatMessage({id: 'client_workspace.users.email'})}
						value={inviteEmail}
						onValueChange={setInviteEmail}
						placeholder={formatMessage({id: 'client_workspace.users.invite_form.email_placeholder'})}
						size="large"
						width="flex"
						data-cy="invite-email-input"
					/>

					<FlexColumn gap={'s'}>
						<Label>
							<FormattedMessage id="client_workspace.users.invite_form.role_label" />
						</Label>
						<select
							value={inviteRole}
							onChange={e => setInviteRole(e.target.value)}
							style={{padding: '8px', width: '100%'}}
							data-cy="invite-role-select"
						>
							{CLIENT_ROLES.map(role => (
								<option key={role.key} value={role.key}>
									{formatMessage({id: role.labelId})}
								</option>
							))}
						</select>
					</FlexColumn>

					<FlexColumn gap={'s'}>
						<Label>
							<FormattedMessage id="client_workspace.users.invite_form.visibility_label" />
						</Label>
						<Checkbox
							checked={inviteScopeLevel === 'client'}
							onChange={() => setInviteScopeLevel('client')}
							labelText={formatMessage({id: 'client_workspace.users.invite_form.whole_org'})}
							data-cy="scope-whole-org"
						/>
						<Checkbox
							checked={inviteScopeLevel === 'scoped'}
							onChange={() => setInviteScopeLevel('scoped')}
							labelText={formatMessage({id: 'client_workspace.users.invite_form.specific_items'})}
							data-cy="scope-specific"
						/>
					</FlexColumn>

					{inviteScopeLevel === 'scoped' && (
						<FlexColumn gap={'s'}>
							{hierarchy.programmes.length > 0 && (
								<FlexColumn gap={'xs'}>
									<Label>
										<FormattedMessage id="client_workspace.users.invite_form.programmes_label" />
									</Label>
									{hierarchy.programmes.map(prog => (
										<Checkbox
											key={prog.id}
											checked={selectedProgrammes.includes(prog.id)}
											onChange={() => toggleProgramme(prog.id)}
											labelText={prog.name}
										/>
									))}
								</FlexColumn>
							)}
							{hierarchy.projects.length > 0 && (
								<FlexColumn gap={'xs'}>
									<Label>
										<FormattedMessage id="client_workspace.users.invite_form.projects_label" />
									</Label>
									{hierarchy.projects.map(proj => (
										<Checkbox
											key={proj.id}
											checked={selectedProjects.includes(proj.id)}
											onChange={() => toggleProject(proj.id)}
											labelText={proj.name}
										/>
									))}
								</FlexColumn>
							)}
						</FlexColumn>
					)}

					<FlexRow gap={'s'}>
						<Button emphasis="medium" onPress={handleInvite} data-cy="submit-invite">
							<FormattedMessage id="client_workspace.users.invite_form.submit" />
						</Button>
						<Button emphasis="low" onPress={() => setShowInviteForm(false)} data-cy="cancel-invite">
							<FormattedMessage id="client_workspace.users.invite_form.cancel" />
						</Button>
					</FlexRow>
				</FlexColumn>
			)}
		</FlexColumn>
	);
};

export default WorkspaceUsersSection;
